import React, { useState } from "react";
import { toast } from "react-toastify";
import apiClient from "../utils/apiConfig";
import errorHandler from '../utils/errorHandler';
import ModalShell from "./ModalShell";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Theme colors for different portals
const themes = {
  user: {
    primary: '#3E7BFA',
    primaryHover: '#0d2d8a',
    buttonBg: '#0A2472',
    infoBg: 'bg-[#0A2472]/10',
    infoBorder: 'border-[#3E7BFA]/30',
    infoText: 'text-[#5B8DEF]',
    focusBorder: 'focus:border-[#3E7BFA]/50'
  },
  admin: {
    primary: '#F43F5E',
    primaryHover: '#BE123C',
    buttonBg: '#9F1239',
    infoBg: 'bg-red-500/10',
    infoBorder: 'border-red-500/30',
    infoText: 'text-red-300',
    focusBorder: 'focus:border-red-500/50'
  },
  nominee: {
    primary: '#14B8A6',
    primaryHover: '#0F766E',
    buttonBg: '#0D9488',
    infoBg: 'bg-[#0D9488]/10',
    infoBorder: 'border-[#0D9488]/30',
    infoText: 'text-[#2DD4BF]',
    focusBorder: 'focus:border-[#0D9488]/50'
  }
};

const MFAVerification = ({ email, tempToken, onSuccess, onCancel, theme = "user" }) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const colors = themes[theme] || themes.user;

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiClient.post(`${API}/mfa/verify-otp`, {
        email,
        otp,
        temp_token: tempToken
      });

      toast.success("Verification successful!");
      onSuccess(response.data);
    } catch (error) {
      errorHandler.handle(error, 'mfa-verification', 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell isOpen onClose={onCancel} maxWidth="max-w-md" className="p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="text-5xl sm:text-6xl mb-4">🔐</div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Verify Your Identity
          </h2>
          <p className="text-gray-300 text-sm sm:text-base">
            We've sent a verification code to
          </p>
          <p className="font-semibold mt-1 text-sm sm:text-base truncate" style={{ color: colors.primary }}>{email}</p>
        </div>

        <form onSubmit={handleVerify}>
          <div className="mb-6">
            <label className="block text-gray-300 mb-2 text-sm">
              Enter 6-digit verification code
            </label>
            <input
              type="text"
              placeholder="000000"
              className={`w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-center text-xl sm:text-2xl tracking-widest placeholder-gray-500 focus:outline-none ${colors.focusBorder} transition-colors font-mono`}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              required
              autoFocus
            />
            <p className="text-gray-400 text-xs mt-2 text-center">
              ⏱️ Code expires in 5 minutes
            </p>
          </div>

          <div className="space-y-3">
            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className={`w-full py-3 text-white font-medium rounded-lg transition ${loading || otp.length !== 6 ? "opacity-50 cursor-not-allowed" : ""
                }`}
              style={{
                backgroundColor: colors.buttonBg,
              }}
              onMouseEnter={(e) => !loading && otp.length === 6 && (e.target.style.backgroundColor = colors.primaryHover)}
              onMouseLeave={(e) => (e.target.style.backgroundColor = colors.buttonBg)}
            >
              {loading ? "Verifying..." : "Verify & Sign In"}
            </button>

            <button
              type="button"
              onClick={onCancel}
              className="w-full px-6 py-3 rounded-lg text-gray-400 hover:text-white transition bg-white/5 hover:bg-white/10 border border-white/10"
            >
              Cancel
            </button>
          </div>
        </form>

        <div className={`mt-6 p-4 ${colors.infoBg} ${colors.infoBorder} border rounded-xl`}>
          <p className={`${colors.infoText} text-sm text-center`}>
            💡 Didn't receive the code? Check your spam folder or try signing in again.
          </p>
        </div>
    </ModalShell>
  );
};

export default MFAVerification;

